import { PushPinOutlined as PinIcon } from "@mui/icons-material";
import { Box, Chip, IconButton, Link as MuiLink, TableCell, TableRow, Tooltip } from "@mui/material";
import { Theme, alpha, useTheme } from "@mui/material/styles";
import React from "react";
import { Link } from "react-router-dom";

import { groupEdge, monospaceStyle, panelShadow } from "../../../theme";
import {
  Matrix,
  MatrixAggregateRow,
  MatrixColumn,
  MatrixProductRow,
  MatrixStance,
  MatrixStanceCell,
} from "../../../types/analytics";
import { EntityClickHandler } from "../../../types/entityRegistry";
import CompareToggle from "../../Compare/CompareToggle";
import { AnalyticsSelection, exploreAddress } from "../analyticsState";
import {
  anyCountedIn,
  cellSelection,
  columnName,
  rowName,
  rowSelection,
  shadeStep,
  strongestStance,
} from "./matrixModel";

// What the table's columns are laid out as: one capability, or a folded group
// of them, with whether a group's left edge is drawn
export interface Slot {
  key: string;
  // One column, or every column of a folded group
  columns: MatrixColumn[];
  folded: boolean;
  // The first slot of a group draws the group's left edge
  groupStart: boolean;
}

// The opacity of each of the five shades of the accent. The first already
// reads as blue on the surface; the count still reads on the last.
export const SHADE_ALPHA: readonly number[] = [0.24, 0.34, 0.45, 0.55, 0.66];

// The opacity of the two stripes of a count under the floor: together fainter
// than the first shade
const THIN_STRIPES = [0.08, 0.22] as const;

// The hatching of a count under the floor, in the accent as the shades are:
// every cell that counts a product is blue, the faintest of them hatched
export const thinHatchOf = (theme: Theme): string => {
  const [faint, full] = THIN_STRIPES.map((opacity) => alpha(theme.palette.accent.main, opacity));
  return `repeating-linear-gradient(45deg, ${faint}, ${faint} 3px, ${full} 3px, ${full} 6px)`;
};

const FIRST_COLUMN_WIDTH = 300;

// A cell of the table: centred, 38px tall, the edge of its group on the left
const cellSx = (slot?: Slot) => ({
  textAlign: "center",
  height: 38,
  p: "3px",
  position: "relative",
  boxSizing: "border-box",
  fontSize: 12.5,
  ...(slot?.groupStart ? { borderLeft: groupEdge } : {}),
});

// The first cell of a row: sticky on the left, the row's name
const rowHeadSx = (theme: Theme, pinned: boolean, dimmed: boolean) => ({
  position: "sticky",
  left: 0,
  zIndex: 2,
  width: FIRST_COLUMN_WIDTH,
  maxWidth: FIRST_COLUMN_WIDTH,
  boxSizing: "border-box",
  px: "12px",
  textAlign: "left",
  fontWeight: 400,
  fontSize: 12.5,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: dimmed ? "text.secondary" : "text.primary",
  // The cell is solid, so that the cells that scroll beneath it do not show
  backgroundColor: theme.palette.background.paper,
  ...(pinned
    ? {
        backgroundImage: `linear-gradient(${alpha(theme.palette.accent.main, 0.08)}, ${alpha(theme.palette.accent.main, 0.08)})`,
        borderLeft: `3px solid ${theme.palette.accent.main}`,
        pl: "9px",
      }
    : {}),
});

const rowSx = (theme: Theme, pinned: boolean) => ({
  ...(pinned ? { backgroundColor: alpha(theme.palette.accent.main, 0.06) } : {}),
});

interface PinButtonProps {
  pinned: boolean;
  onToggle: () => void;
}

// Pins a row first on every page, or lets it go; seen on hover and always on
// the pinned row
const PinButton: React.FC<PinButtonProps> = ({ pinned, onToggle }) => (
  <IconButton
    size="small"
    aria-label={pinned ? "Unpin row" : "Pin row"}
    onClick={onToggle}
    sx={{
      p: "2px",
      mr: 0.75,
      verticalAlign: "middle",
      opacity: pinned ? 1 : 0,
      color: pinned ? "accent.main" : "text.secondary",
      "&:focus-visible, .MuiTableRow-root:hover &": { opacity: 1 },
    }}
  >
    <PinIcon sx={{ fontSize: 15 }} />
  </IconButton>
);

const STANCE_NAMES: Record<MatrixStance, string> = {
  has: "has",
  requires: "required",
  lacks: "lacking",
};

// The dot of a has, the hollow amber dot of a requires, the cross of a lacks.
// A decorative one (in the legend) is hidden from assistive technology.
export const StanceMark: React.FC<{ stance: MatrixStance; decorative?: boolean }> = ({
  stance,
  decorative = false,
}) => {
  const label = STANCE_NAMES[stance];
  const naming = decorative
    ? ({ "aria-hidden": true } as const)
    : ({ role: "img", "aria-label": label } as const);
  if (stance === "has") {
    return (
      <Box
        component="span"
        {...naming}
        sx={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: "link.main",
        }}
      />
    );
  }
  if (stance === "requires") {
    return (
      <Box
        component="span"
        {...naming}
        sx={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          border: (theme) => `2px solid ${theme.palette.chart.need}`,
          boxSizing: "content-box",
        }}
      />
    );
  }
  return (
    <Box
      component="span"
      {...naming}
      sx={{ color: "text.disabled", opacity: 0.8, fontSize: 12 }}
    >
      ✕
    </Box>
  );
};

const names = (refs: MatrixStanceCell["conditions"]): string =>
  refs.map((ref) => ref.name ?? ref.id).join(", ");

// What a stance cell says on hover
const StanceTip: React.FC<{ product: string; column: string; cell: MatrixStanceCell }> = ({
  product,
  column,
  cell,
}) => {
  let detail: string;
  if (cell.stance === "requires") {
    detail = "stated as required";
  } else if (cell.stance === "lacks") {
    detail = "stated as lacking";
  } else {
    const parts = [
      ...(cell.conditions.length > 0 ? [`claimed under ${names(cell.conditions)}`] : []),
      ...(cell.modes.length > 0 ? [`mode ${names(cell.modes)}`] : []),
    ];
    detail = parts.length > 0 ? parts.join(" · ") : "no conditions or modes stated";
  }
  return (
    <Box sx={{ fontSize: 12.5, lineHeight: 1.45, p: 0.25 }}>
      <Box sx={{ fontWeight: 600 }}>
        {product} · {column}
      </Box>
      <Box>{detail}</Box>
    </Box>
  );
};

const TOOLTIP_SLOTS = {
  tooltip: {
    sx: {
      maxWidth: 320,
      backgroundColor: "background.raised",
      color: "text.primary",
      border: 1,
      borderColor: (theme: Theme) => alpha(theme.palette.chart.ink, 0.14),
      boxShadow: (theme: Theme) => panelShadow(theme, 0.55),
    },
  },
  arrow: { sx: { color: "background.raised" } },
} as const;

interface ProductRowProps {
  row: MatrixProductRow;
  slots: Slot[];
  onPin: (id: string | null) => void;
  onOpenEntity: EntityClickHandler;
}

// A product: its name and maker, a mark for each column it has a claim in,
// and the number of columns it has
export const ProductRow: React.FC<ProductRowProps> = ({ row, slots, onPin, onOpenEntity }) => {
  const theme = useTheme();
  const product = row.product.name ?? row.product.id;
  return (
    <TableRow data-pinned={row.pinned} sx={rowSx(theme, row.pinned)}>
      <TableCell component="th" scope="row" sx={rowHeadSx(theme, row.pinned, false)}>
        <CompareToggle id={row.product.id} name={row.product.name} />
        <PinButton pinned={row.pinned} onToggle={() => onPin(row.pinned ? null : row.id)} />
        <MuiLink
          component="button"
          type="button"
          underline="hover"
          color="text.primary"
          onClick={() => onOpenEntity(row.product.id, "product")}
          sx={{ font: "inherit", verticalAlign: "baseline" }}
        >
          {product}
        </MuiLink>
        <Box component="span" sx={{ color: "text.secondary", fontSize: 12 }}>
          {" · "}
          {row.maker ? (
            <>
              <MuiLink
                component="button"
                type="button"
                underline="hover"
                onClick={() => onOpenEntity(row.maker!.id, "organization")}
                sx={{ font: "inherit", verticalAlign: "baseline" }}
              >
                {row.maker.name ?? row.maker.id}
              </MuiLink>
              {row.country && (
                <>
                  {" · "}
                  <Box component="span" sx={monospaceStyle}>
                    {row.country}
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box component="span" sx={{ color: "text.disabled" }}>
              no maker on record
            </Box>
          )}
        </Box>
        {row.pinned && (
          <Chip
            label="pinned"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 0.75, height: 20, fontSize: 11.5 }}
          />
        )}
      </TableCell>
      {slots.map((slot) => {
        if (slot.folded) {
          const stance = strongestStance(row, slot.columns);
          return (
            <TableCell key={slot.key} sx={{ ...cellSx(slot), backgroundColor: "action.hover" }}>
              {stance && (
                <Tooltip
                  describeChild
                  arrow
                  placement="top"
                  slotProps={TOOLTIP_SLOTS}
                  title={
                    <Box sx={{ fontSize: 12.5 }}>
                      {product} · any of {slot.columns.length}
                    </Box>
                  }
                >
                  <Box component="span" sx={{ display: "inline-block" }}>
                    <StanceMark stance={stance} />
                  </Box>
                </Tooltip>
              )}
            </TableCell>
          );
        }
        const column = slot.columns[0];
        const cell = row.cells[column.id];
        return (
          <TableCell key={slot.key} sx={cellSx(slot)}>
            {cell && (
              <Tooltip
                describeChild
                arrow
                placement="top"
                slotProps={TOOLTIP_SLOTS}
                title={<StanceTip product={product} column={columnName(column)} cell={cell} />}
              >
                <Box component="span" sx={{ display: "inline-block" }}>
                  <StanceMark stance={cell.stance} />
                </Box>
              </Tooltip>
            )}
          </TableCell>
        );
      })}
      <TableCell sx={{ ...cellSx(), ...monospaceStyle, color: "text.secondary" }}>
        {row.claim_count}
      </TableCell>
    </TableRow>
  );
};

interface AggregateRowProps {
  matrix: Matrix;
  row: MatrixAggregateRow;
  slots: Slot[];
  selection: AnalyticsSelection;
  onPin: (id: string | null) => void;
  onOpenEntity: EntityClickHandler;
}

// The count of a cell on its shade, hatched and dimmed under the floor, with
// the products stated as a need in the corner; a link to the products it
// counts
const CountCell: React.FC<{
  matrix: Matrix;
  row: MatrixAggregateRow;
  column: MatrixColumn;
  selection: AnalyticsSelection;
  slot: Slot;
}> = ({ matrix, row, column, selection, slot }) => {
  const theme = useTheme();
  const cell = row.cells[column.id];
  if (!cell) {
    return (
      <TableCell sx={cellSx(slot)}>
        <Box component="span" sx={{ color: "text.disabled", opacity: 0.8 }} aria-label="none">
          ·
        </Box>
      </TableCell>
    );
  }
  const step = shadeStep(cell.count, column.product_count);
  const picked = cell.count > 0 ? cellSelection(selection, row, column, matrix) : null;
  return (
    <TableCell sx={cellSx(slot)}>
      <Box
        data-shade={cell.thin ? 0 : step}
        data-thin={cell.thin}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 32,
          borderRadius: "4px",
          position: "relative",
          ...monospaceStyle,
          ...(cell.thin
            ? { color: "text.secondary", backgroundImage: thinHatchOf(theme) }
            : step > 0
              ? { backgroundColor: alpha(theme.palette.accent.main, SHADE_ALPHA[step - 1]) }
              : { color: "text.disabled" }),
        }}
      >
        {picked ? (
          <MuiLink
            component={Link}
            to={exploreAddress(picked)}
            underline="hover"
            color="inherit"
            sx={{ ...monospaceStyle, display: "flex", width: "100%", height: "100%", alignItems: "center", justifyContent: "center" }}
          >
            {cell.count}
          </MuiLink>
        ) : (
          cell.count
        )}
        {cell.need_count > 0 && (
          <Box
            component="span"
            title={`${cell.need_count} stated as required or lacking`}
            sx={{
              position: "absolute",
              top: 2,
              right: 5,
              fontSize: 9.5,
              lineHeight: 1,
              color: theme.palette.chart.need,
            }}
          >
            {cell.need_count}
          </Box>
        )}
      </Box>
    </TableCell>
  );
};

// A class, a maker or a capability: its name, the products it counts, a count
// in each column, and its products at the row's end
export const AggregateRow: React.FC<AggregateRowProps> = ({
  matrix,
  row,
  slots,
  selection,
  onPin,
  onOpenEntity,
}) => {
  const theme = useTheme();
  const name = rowName(row);
  const picked = rowSelection(selection, row, matrix.rows);
  const address = picked ? exploreAddress(picked) : null;
  const count = (
    <Box component="span" sx={{ ...monospaceStyle, color: "text.secondary", ml: 1, fontSize: 12 }}>
      {row.product_count}
    </Box>
  );
  return (
    <TableRow data-pinned={row.pinned} sx={rowSx(theme, row.pinned)}>
      <TableCell component="th" scope="row" sx={rowHeadSx(theme, row.pinned, row.thin)}>
        <PinButton pinned={row.pinned} onToggle={() => onPin(row.pinned ? null : row.id)} />
        {matrix.rows === "makers" && row.entity ? (
          <>
            <MuiLink
              component="button"
              type="button"
              underline="hover"
              color="text.primary"
              onClick={() => onOpenEntity(row.entity!.id, row.entity!.type)}
              sx={{ font: "inherit", verticalAlign: "baseline" }}
            >
              {name}
            </MuiLink>
            {row.country && (
              <Box component="span" sx={{ ...monospaceStyle, color: "text.secondary", ml: 1, fontSize: 12 }}>
                {row.country}
              </Box>
            )}
            {address ? (
              <MuiLink component={Link} to={address} underline="hover" sx={{ ...monospaceStyle, ml: 1, fontSize: 12 }}>
                {row.product_count}
              </MuiLink>
            ) : (
              count
            )}
          </>
        ) : address ? (
          <>
            <MuiLink component={Link} to={address} underline="hover" color="text.primary">
              {name}
            </MuiLink>
            {count}
          </>
        ) : (
          <>
            {name}
            {count}
          </>
        )}
        {row.thin && (
          <Chip
            label="under the floor"
            size="small"
            variant="outlined"
            sx={{ ml: 0.75, height: 20, fontSize: 11.5, color: "text.secondary" }}
          />
        )}
        {row.pinned && (
          <Chip
            label="pinned"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 0.75, height: 20, fontSize: 11.5 }}
          />
        )}
      </TableCell>
      {slots.map((slot) => {
        if (slot.folded) {
          return (
            <TableCell key={slot.key} sx={{ ...cellSx(slot), backgroundColor: "action.hover" }}>
              {anyCountedIn(row, slot.columns) ? (
                <Box
                  component="span"
                  role="img"
                  aria-label="some"
                  title={`Some products in the group of ${slot.columns.length}`}
                  sx={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "link.main",
                  }}
                />
              ) : (
                <Box component="span" sx={{ color: "text.disabled", opacity: 0.8 }}>
                  ·
                </Box>
              )}
            </TableCell>
          );
        }
        return (
          <CountCell
            key={slot.key}
            matrix={matrix}
            row={row}
            column={slot.columns[0]}
            selection={selection}
            slot={slot}
          />
        );
      })}
      <TableCell sx={{ ...cellSx(), ...monospaceStyle, color: "text.secondary" }}>
        {row.product_count}
      </TableCell>
    </TableRow>
  );
};

interface TotalsRowProps {
  matrix: Matrix;
  slots: Slot[];
}

// The column counts under an aggregate table, over the whole scope
export const TotalsRow: React.FC<TotalsRowProps> = ({ matrix, slots }) => {
  const theme = useTheme();
  return (
    <TableRow
      sx={{
        "& .MuiTableCell-root": {
          color: "text.secondary",
          borderTop: `1px solid ${alpha(theme.palette.chart.ink, 0.14)}`,
          borderBottom: 0,
        },
      }}
    >
      <TableCell component="th" scope="row" sx={rowHeadSx(theme, false, false)}>
        All in scope
        <Box component="span" sx={{ ...monospaceStyle, ml: 1, fontSize: 12 }}>
          {matrix.evidence.n}
        </Box>
      </TableCell>
      {slots.map((slot) => (
        <TableCell
          key={slot.key}
          sx={{ ...cellSx(slot), ...monospaceStyle, ...(slot.folded ? { backgroundColor: "action.hover" } : {}) }}
        >
          {slot.folded ? null : slot.columns[0].product_count}
        </TableCell>
      ))}
      <TableCell sx={{ ...cellSx(), ...monospaceStyle }}>{matrix.evidence.n}</TableCell>
    </TableRow>
  );
};
